import React, { useState } from "react";
import Logo from "../../helper/svg/LOGO.svg";
import Spoons from "../../helper/svg/spoons.svg";
import Hotels from "../../helper/svg/hotel.svg";
import Mobile from "../../helper/svg/mobile.svg";
import { Link, useNavigate } from "react-router-dom";
import { AiFillEyeInvisible } from "react-icons/ai";
import { Eye } from "lucide-react";
import { signup } from "../../api/auth";
import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "../../hooks/useTranslation";
import { allCountries } from 'country-telephone-data';


const Register = () => {
  const { t } = useTranslation();
  const registerCopy = t("register") || {};
  const registerLeft = registerCopy.left || {};
  const registerTabs = registerCopy.tabs || {};
  const registerErrors = registerCopy.errors || {};
  const registerToast = registerCopy.toast || {};
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [iban, setIban] = useState("");
  const [role, setRole] = useState("user");
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false); // ✅ new state
  const [selected, setSelected] = useState("user");
  const [countryCode, setCountryCode] = useState("995");
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const signupMutation = useMutation({
    mutationFn: signup,
    onSuccess: () => {
      toast.success(registerToast.success || "User registered successfully.");
      const last5Digits = phoneNumber.slice(-5);
      const first5Digits = phoneNumber.slice(0, 5);
      navigate(`/otpverification?email=${email}&mobile=${first5Digits}`);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        registerToast.error ||
        "Error in signup.";
      toast.error(errorMessage);
    },
  });

  // Validate name input
  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    
    // Allow only letters (including international), spaces, hyphens, apostrophes, and dots
    // This regex supports English and international characters
    if (value && !/^[\p{L}\s\-'\.]+$/u.test(value)) {
      setNameError(
        registerErrors.nameFormat ||
          "Name can only contain letters, spaces, hyphens, apostrophes, and dots."
      );
    } else {
      setNameError("");
    }
  };

  // Validate phone number input
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits - automatically remove any non-digit characters
    const numericValue = value.replace(/\D/g, '');
    
    // Show error if user tried to enter non-digits
    if (value !== numericValue && value.length > 0) {
      setPhoneError(
        registerErrors.phoneDigits || "Phone number can only contain digits."
      );
    } else {
      setPhoneError("");
    }
    
    setPhoneNumber(numericValue);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    // Validate name
    if (!name) {
      setNameError(registerErrors.nameRequired || "Name is required.");
      toast.error(registerToast.fillAll || "Please fill all required fields.");
      return;
    }
    if (!/^[\p{L}\s\-'\.]+$/u.test(name)) {
      const nameFormatMessage =
        registerErrors.nameFormat ||
        "Name can only contain letters, spaces, hyphens, apostrophes, and dots.";
      setNameError(nameFormatMessage);
      toast.error(nameFormatMessage);
      return;
    }

    // Validate phone number
    if (!phoneNumber) {
      setPhoneError(
        registerErrors.phoneRequired || "Phone number is required."
      );
      toast.error(registerToast.fillAll || "Please fill all required fields.");
      return;
    }
    if (!/^\d+$/.test(phoneNumber)) {
      const phoneDigitsMessage =
        registerErrors.phoneDigits || "Phone number can only contain digits.";
      setPhoneError(phoneDigitsMessage);
      toast.error(phoneDigitsMessage);
      return;
    }

    if (!email || !password || !confirmPassword) {
      toast.error(registerToast.fillAll || "Please fill all required fields.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error(
        registerToast.passwordMismatch || "Passwords do not match."
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(registerToast.invalidEmail || "Invalid email format.");
      return;
    }

    if (role === "owner" && !iban) {
      toast.error(registerToast.ibanRequired || "IBAN is required for owners.");
      return;
    }

    if (!acceptTerms) {
      toast.error(
        registerToast.termsRequired ||
          "You must accept the Terms and Conditions."
      );
      return;
    }

    setLoading(true);

    const fullPhoneNumber = `+${countryCode}${phoneNumber}`;

    signupMutation.mutate(
      {
        name,
        email,
        password,
        confirmPassword,
        phoneNumber: fullPhoneNumber,
        promoCode,
        iban,
        role: selected,
      },
      {
        onSettled: () => setLoading(false),
      }
    );
  };

  return (
    <div className="grid lg:grid-cols-2 grid-cols-1 h-screen">
      {/* Left side */}
      <div className="bg-[#FFF3EF] flex items-center lg:justify-start justify-center">
        <div className="w-full max-w-[650px] lg:ml-auto xl:pr-[175px] lg:pr-[100px] md:py-[20px] py-[30px] px-[15px]">
          <Link to="/">
            <img src={Logo} alt="logo" className="w-[133px]" />
          </Link>

          {[
            {
              icon: Spoons,
              title: registerLeft.tableTitle,
              desc: registerLeft.tableDesc,
            },
            {
              icon: Hotels,
              title: registerLeft.hotelTitle,
              desc: registerLeft.hotelDesc,
            },
            {
              icon: Mobile,
              title: registerLeft.opsTitle,
              desc: registerLeft.opsDesc,
            },
          ].map((item, index) => (
            <div
              key={index}
              className="grid grid-cols-[59px_1fr] gap-[17px] mt-[51px] w-full mb-[35px]"
            >
              <div className="w-[59px] h-[59px] rounded-[5px] bg-[#4CAF50] flex items-center justify-center">
                <img src={item.icon} alt="Icon" className="w-[37px]" />
              </div>
              <div className="w-full">
                <h2 className="md:text-[22px] text-lg font-semibold leading-[22px] mb-[7px]">
                  {item.title}
                </h2>
                <p className="text-base leading-[26px]">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center lg:justify-start justify-center lg:pl-[72px] md:pl-[20px] md:py-[20px] py-[30px] px-[15px] w-full max-w-[100%]">
        <div className="w-full max-w-[586px]">

          <form
            onSubmit={handleSignUp}
            className="w-full md:py-[40px] md:px-[27px] py-[25px] px-[15px] rounded-[10px] form-shadow"
          >
            <h2 className="md:text-[30px] text-[22px] font-medium md:mb-[45px] mb-[20px] text-center leading-[21px] placeholder:text-[#858585]">
              {registerCopy.title}
            </h2>


            <div className="flex items-center justify-center mx-auto bg-[#D4EFD5] border border-[#4CAF50] rounded-full md:overflow-hidden  w-full mb-5">
              <button type="button"
                className={`basis-1/2 lg:px-[57px] lg:py-[19px]  md:py-3 md:px-6 p-4  rounded-full lg:text-[20px] text-base transition-all lg:font-bold font-semibold  ${selected === "user"
                  ? "bg-[#4CAF50] text-white lg:px-[57px] lg:py-[19px]  md:py-3 md:px-6 p-4"
                  : "bg-transparent text-black"
                  }`}
                onClick={() => setSelected("user")}
              >
                {registerTabs.user || "User"}
              </button>

              <button type="button"
                className={`basis-1/2 lg:px-[57px] lg:py-[19px]  md:py-3 md:px-6 p-4  rounded-full lg:text-[20px] text-base transition-all lg:font-bold font-semibold  ${selected === "owner"
                  ? "bg-[#4CAF50] text-white lg:px-[57px] lg:py-[19px]  md:py-3 md:px-6 p-4"
                  : "bg-transparent text-black"
                  }`}
                onClick={() => setSelected("owner")}
              >
                {registerTabs.owner || "Owner"}
              </button>
            </div>

            {/* Inputs */}
            <div className="w-full mb-[15px]">
              <input
                type="text"
                placeholder={registerCopy.namePlaceholder}
                value={name}
                onChange={handleNameChange}
                className={`w-full rounded-[10px] px-[20px] outline-none py-[12px] border text-[18px] placeholder:text-[#858585] ${
                  nameError ? "border-red-500" : "border-[#ECEBEB]"
                }`}
              />
              {nameError && (
                <p className="text-red-500 text-sm mt-1">{nameError}</p>
              )}
            </div>
            <input
              type="text"
              placeholder={registerCopy.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mb-[15px] rounded-[10px] px-[20px] outline-none py-[12px] border border-[#ECEBEB] text-[18px] placeholder:text-[#858585]"
            />

            {/* Password */}
            <div className="relative w-full">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={registerCopy.passwordPlaceholder}
                className="w-full mb-[15px] rounded-[10px] px-[20px] outline-none py-[19px] border border-grey-500 text-[18px] placeholder:text-[#858585] pr-10"
              />
              {password && (
                <div
                  className="absolute top-[40%] translate-y-[-40%] right-3 flex items-center cursor-pointer text-gray-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <AiFillEyeInvisible size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="relative w-full">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={registerCopy.confirmPasswordPlaceholder}
                className="w-full mb-[15px] rounded-[10px] px-[20px] outline-none py-[19px] border border-[#ECEBEB] text-[18px] placeholder:text-[#858585] pr-10"
              />
              {confirmPassword && (
                <div
                  className="absolute top-[40%] translate-y-[-40%] right-3 flex items-center cursor-pointer text-gray-400"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <AiFillEyeInvisible size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </div>
              )}
            </div>

            {/* <input
              type="tel"
              placeholder={t("register").phonePlaceholder}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full mb-[15px] rounded-[10px] px-[20px] outline-none py-[19px] border border-[#ECEBEB] text-[18px] placeholder:text-[#858585]"
            /> */}

<div className="w-full mb-[15px]"><select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full flex-shrink-0 rounded-[10px] border border-[#ECEBEB] px-[10px] py-[12px] text-[16px] outline-none"
              >
                {allCountries.map((c) => (
                  <option key={c.iso2} value={c.dialCode}>
                    +{c.dialCode} ({c.name})
                  </option>
                ))}
              </select> </div>

            <div className="mb-[15px]">
              <div className="flex gap-2">
                <input
                  type="tel"
                  placeholder={registerCopy.phonePlaceholder}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  className={`flex-1 rounded-[10px] px-[20px] py-[12px] outline-none border text-[16px] placeholder:text-[#858585] ${
                    phoneError ? "border-red-500" : "border-[#ECEBEB]"
                  }`}
                />
              </div>
              {phoneError && (
                <p className="text-red-500 text-sm mt-1">{phoneError}</p>
              )}
            </div>



            {role === "owner" && (
              <input
                type="text"
                value={iban}
                onChange={(e) => setIban(e.target.value)}
                placeholder={registerCopy.ibanPlaceholder}
                className="w-full mb-[15px] rounded-[10px] px-[20px] outline-none py-[19px] border border-[#ECEBEB] text-[18px] placeholder:text-[#858585]"
              />
            )}

            {/* Promo Code (Optional) */}
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder={registerCopy.promoCodePlaceholder || "Promo Code (Optional)"}
              className="w-full mb-[15px] rounded-[10px] px-[20px] outline-none py-[19px] border border-[#ECEBEB] text-[18px] placeholder:text-[#858585]"
            />

            {/* ✅ Terms and Conditions checkbox */}
            <div className="flex items-start gap-2 mb-[15px]">
              <input
                type="checkbox"
                id="terms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-[6px]"
              />
              <label htmlFor="terms" className="text-sm text-gray-700">
                {registerCopy.acceptTerms}{" "}
                <Link to="/terms" className="text-[#FFA808] underline">
                  {registerCopy.termsLink}
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-[2px] bg-[#4CAF50] text-[18px] text-white font-semibold p-[19px] green-shadow hover:bg-[#209b25]"
            >
              {loading ? t("header").loading :  registerCopy.button}
            </button>
          </form>

          <p className="text-center text-[18px] text-[#2E2E2E] md:mt-[42px] mt-[20px]">
            {registerCopy.alreadyAccount}{" "}
            <Link className="text-[#FFA808] underline" to="/login">
              {registerCopy.login}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
